/**
 * AuditLogs — /admin/audit-logs
 *
 * Wave 6 Batch 3 · Cluster A — Log-Style chrome sweep.
 * Anchor: CreditEngineEvents (Wave 5-Late).
 *
 *   • AdminLayout wrap (unchanged).
 *   • AdminPageHelp lifted out of `actions` (previously mis-nested).
 *   • `_primitives.Section` for filters + events table.
 *   • `_primitives.SelectInput` / `TextInput` for filters.
 *   • `_primitives.GhostButton` for Refresh / Export.
 *   • SEVERITY_COLOR pastel map REMOVED. `Pill` now emits a DS `Badge`
 *     with `shape="sharp"` + SEVERITY_TONE map. Action-type pill uses
 *     `tone="accent"` (semantic accent for admin actions).
 *   • Dense hairline table `px-4 py-1.5` + `isLast` guard.
 *   • Detail modal chrome swept to `--ds-*` tokens.
 *
 * Every testid, API call, threshold preserved BYTE-EXACT.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { ArrowLeft, Download, RefreshCw, Search, X } from "lucide-react";
import { toast } from "sonner";
import AdminLayout from "@/admin/AdminLayout";
import AdminPageHelp from "@/design-system/components/AdminPageHelp";
import { Badge } from "@/design-system/components/Badge";
import { Heading } from "@/design-system/components/Heading";
import { api, formatApiError } from "@/api/client";
import { Section, SelectInput, TextInput, GhostButton } from "@/admin/_primitives";

const SEVERITIES = ["info", "warning", "critical"];
const SEVERITY_TONE = {
  info:     "neutralMuted",
  warning:  "warning",
  critical: "danger",
};

function ActionPill({ value }) {
  if (!value) return <span style={{ color: "var(--ds-color-ink-subtle)" }}>—</span>;
  return (
    <Badge tone="accent" size="sm" shape="sharp" className="uppercase tracking-[0.1em]">
      {String(value).replace(/_/g, " ")}
    </Badge>
  );
}

function SeverityPill({ value }) {
  if (!value) return <span style={{ color: "var(--ds-color-ink-subtle)" }}>—</span>;
  return (
    <Badge
      tone={SEVERITY_TONE[value] || "neutralMuted"}
      size="sm"
      shape="sharp"
      className="uppercase tracking-[0.1em]"
    >
      {value}
    </Badge>
  );
}

export default function AdminAuditLogs() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [actionType, setActionType] = useState("");
  const [targetType, setTargetType] = useState("");
  const [severity, setSeverity] = useState("");
  const [actorEmail, setActorEmail] = useState("");
  const [debouncedActor, setDebouncedActor] = useState("");
  const [offset, setOffset] = useState(0);
  const [openRow, setOpenRow] = useState(null);

  useEffect(() => { const t = setTimeout(() => setDebouncedActor(actorEmail), 250); return () => clearTimeout(t); }, [actorEmail]);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (actionType) params.set("action_type", actionType);
      if (targetType) params.set("target_type", targetType);
      if (severity) params.set("severity", severity);
      if (debouncedActor) params.set("actor_email", debouncedActor);
      params.set("limit", "50");
      params.set("offset", String(offset));
      const { data } = await api.get(`/admin/audit-logs?${params.toString()}`);
      setItems(data?.items || []);
      setTotal(data?.total || 0);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [actionType, targetType, severity, debouncedActor, offset]);

  useEffect(() => { reload(); }, [reload]);

  const actionOptions = useMemo(() => Array.from(new Set(items.map((i) => i.action_type).filter(Boolean))).sort(), [items]);
  const targetOptions = useMemo(() => Array.from(new Set(items.map((i) => i.target_type).filter(Boolean))).sort(), [items]);

  function exportCsv() {
    const params = new URLSearchParams();
    if (actionType) params.set("action_type", actionType);
    if (targetType) params.set("target_type", targetType);
    if (severity) params.set("severity", severity);
    if (debouncedActor) params.set("actor_email", debouncedActor);
    const base = `${process.env.REACT_APP_BACKEND_URL || ""}/api/admin/audit-logs/export.csv`;
    window.open(`${base}?${params.toString()}`, "_blank");
  }

  return (
    <AdminLayout
      title="Audit logs"
      subtitle={`${total} admin actions recorded`}
      actions={
        <div className="flex items-center gap-2">
          <GhostButton data-testid="audit-logs-export" onClick={exportCsv}>
            <Download className="w-3 h-3" /> Export CSV
          </GhostButton>
          <GhostButton data-testid="audit-logs-refresh" onClick={reload}>
            <RefreshCw className="w-3 h-3" /> Refresh
          </GhostButton>
        </div>
      }
    >
      <div className="mb-3 max-w-3xl">
        <AdminPageHelp
          title="Audit Logs"
          purpose="System-wide immutable audit trail for admin actions, permission changes, and sensitive operations."
          configures="Nothing — read-only. Filter by actor, action type, and time window."
          impact="No user-facing impact. Records are the source of truth for compliance reviews."
          safety="Read-only and immutable. Records cannot be edited or deleted from this UI."
        />
      </div>

      <Section title="Filter" testid="audit-logs-filter-section">
        <div className="grid md:grid-cols-[1fr_repeat(3,auto)] gap-2 items-end">
          <label className="block relative">
            <Search
              className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none"
              style={{ color: "var(--ds-color-ink-subtle)" }}
              aria-hidden="true"
            />
            <TextInput
              type="text"
              value={actorEmail}
              onChange={(e) => { setOffset(0); setActorEmail(e.target.value); }}
              placeholder="Search by actor email…"
              data-testid="audit-logs-search-actor"
              className="w-full pl-9"
            />
          </label>
          <SelectInput
            value={actionType}
            onChange={(e) => { setOffset(0); setActionType(e.target.value); }}
            data-testid="audit-logs-filter-action"
          >
            <option value="">All actions</option>
            {actionOptions.map((a) => <option key={a} value={a}>{a}</option>)}
          </SelectInput>
          <SelectInput
            value={targetType}
            onChange={(e) => { setOffset(0); setTargetType(e.target.value); }}
            data-testid="audit-logs-filter-target"
          >
            <option value="">All target types</option>
            {targetOptions.map((t) => <option key={t} value={t}>{t}</option>)}
          </SelectInput>
          <SelectInput
            value={severity}
            onChange={(e) => { setOffset(0); setSeverity(e.target.value); }}
            data-testid="audit-logs-filter-severity"
          >
            <option value="">All severities</option>
            {SEVERITIES.map((s) => <option key={s} value={s}>{s}</option>)}
          </SelectInput>
        </div>
      </Section>

      {loading ? (
        <div className="text-[13px] py-6 text-center" style={{ color: "var(--ds-color-ink-muted)" }}>
          Loading…
        </div>
      ) : (
        <Section
          title="Audit records"
          testid="audit-logs-section"
          bodyClassName="p-0"
          badge={
            <span
              className="text-[11px] uppercase tracking-[0.14em] tabular-nums"
              style={{ color: "var(--ds-color-ink-muted)" }}
            >
              {items.length} showing · {total} total
            </span>
          }
        >
          <div className="overflow-x-auto" data-testid="audit-logs-table">
            <table className="w-full text-[13px]">
              <thead>
                <tr
                  className="text-left text-[10px] uppercase tracking-[0.16em] font-semibold"
                  style={{
                    color: "var(--ds-color-ink-subtle)",
                    background: "var(--ds-color-admin-surface-muted)",
                    borderBottom: "1px solid var(--ds-color-admin-border)",
                  }}
                >
                  <th className="px-4 py-2">When</th>
                  <th className="px-4 py-2">Actor</th>
                  <th className="px-4 py-2">Action</th>
                  <th className="px-4 py-2">Target</th>
                  <th className="px-4 py-2">Severity</th>
                  <th className="px-4 py-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((row, idx) => {
                  const isLast = idx === items.length - 1;
                  return (
                    <tr
                      key={row.id}
                      data-testid={`audit-logs-row-${row.id}`}
                      className="cursor-pointer transition-colors"
                      onClick={() => setOpenRow(row)}
                      style={{
                        borderBottom: isLast ? "none" : "1px solid var(--ds-color-admin-border)",
                        transitionDuration: "var(--ds-duration-fast)",
                      }}
                      onMouseEnter={(e) =>
                        (e.currentTarget.style.background = "var(--ds-color-admin-surface-muted)")
                      }
                      onMouseLeave={(e) =>
                        (e.currentTarget.style.background = "transparent")
                      }
                    >
                      <td
                        className="px-4 py-1.5 text-[12px] whitespace-nowrap tabular-nums font-mono"
                        style={{ color: "var(--ds-color-ink-muted)" }}
                      >
                        {row.created_at?.slice ? row.created_at.replace("T", " ").slice(0, 19) : "—"}
                      </td>
                      <td className="px-4 py-1.5">
                        <div style={{ color: "var(--ds-color-ink)" }}>{row.actor_email}</div>
                        <div className="text-[10px]" style={{ color: "var(--ds-color-ink-muted)" }}>
                          {row.actor_team_role || "—"}
                        </div>
                      </td>
                      <td className="px-4 py-1.5"><ActionPill value={row.action_type} /></td>
                      <td className="px-4 py-1.5 text-xs">
                        <div style={{ color: "var(--ds-color-ink)" }}>
                          {row.target_label || row.target_id || "—"}
                        </div>
                        <div className="text-[10px]" style={{ color: "var(--ds-color-ink-muted)" }}>
                          {row.target_type}
                        </div>
                      </td>
                      <td className="px-4 py-1.5"><SeverityPill value={row.severity} /></td>
                      <td className="px-4 py-1.5 text-right">
                        <span
                          className="text-[11px] uppercase tracking-[0.12em] font-semibold"
                          style={{ color: "var(--ds-color-accent-strong)" }}
                        >
                          Open
                        </span>
                      </td>
                    </tr>
                  );
                })}
                {items.length === 0 && (
                  <tr>
                    <td
                      colSpan="6"
                      className="px-4 py-8 text-center text-[13px]"
                      style={{ color: "var(--ds-color-ink-muted)" }}
                    >
                      No audit rows match.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </Section>
      )}

      <div
        className="flex items-center justify-between mt-2 text-[11px] tabular-nums"
        style={{ color: "var(--ds-color-ink-muted)" }}
      >
        <span>Page offset {offset} of {total}</span>
        <div className="flex gap-1.5">
          <GhostButton
            onClick={() => setOffset(Math.max(0, offset - 50))}
            disabled={offset === 0}
            className="!py-1 !text-[11px]"
          >
            Prev
          </GhostButton>
          <GhostButton
            onClick={() => setOffset(offset + 50)}
            disabled={offset + 50 >= total}
            className="!py-1 !text-[11px]"
          >
            Next
          </GhostButton>
        </div>
      </div>

      {openRow && <AuditDetail row={openRow} onClose={() => setOpenRow(null)} />}
    </AdminLayout>
  );
}

function AuditDetail({ row, onClose }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(27, 26, 22, 0.55)" }}
      onClick={onClose}
    >
      <div
        data-testid="audit-logs-modal"
        className="w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-[var(--ds-radius-card)]"
        style={{
          background: "var(--ds-color-surface)",
          border: "1px solid var(--ds-color-admin-border)",
          boxShadow: "0 1px 0 0 var(--ds-color-admin-border)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <header
          className="sticky top-0 flex items-center justify-between px-5 py-3.5 z-10 border-b"
          style={{
            background: "var(--ds-color-surface)",
            borderColor: "var(--ds-color-admin-border)",
          }}
        >
          <div className="flex items-center gap-3 min-w-0">
            <button
              type="button"
              onClick={onClose}
              data-testid="audit-logs-modal-back"
              className="inline-flex items-center gap-1 text-[12px] uppercase tracking-[0.12em] transition-colors"
              style={{ color: "var(--ds-color-ink-muted)" }}
              onMouseEnter={(e) => (e.currentTarget.style.color = "var(--ds-color-ink)")}
              onMouseLeave={(e) => (e.currentTarget.style.color = "var(--ds-color-ink-muted)")}
            >
              <ArrowLeft className="w-3.5 h-3.5" /> Back to list
            </button>
            <div className="min-w-0">
              <div
                className="text-[10px] uppercase tracking-[0.18em] font-semibold"
                style={{ color: "var(--ds-color-accent-strong)" }}
              >
                Audit record
              </div>
              <Heading level={4} size="md" weight="semibold" className="truncate">
                {row.action_type}
              </Heading>
            </div>
          </div>
          <button
            onClick={onClose}
            className="transition-colors"
            style={{ color: "var(--ds-color-ink-muted)" }}
            onMouseEnter={(e) => (e.currentTarget.style.color = "var(--ds-color-ink)")}
            onMouseLeave={(e) => (e.currentTarget.style.color = "var(--ds-color-ink-muted)")}
          >
            <X className="w-5 h-5" />
          </button>
        </header>
        <div className="p-5 space-y-3">
          <Meta label="When" value={row.created_at?.slice ? row.created_at.replace("T", " ").slice(0, 19) : row.created_at} />
          <Meta label="Actor" value={`${row.actor_email} (${row.actor_team_role || "—"})`} />
          <Meta label="Target" value={`${row.target_type} · ${row.target_label || row.target_id || "—"}`} />
          <Meta label="Severity" value={row.severity} />
          <Meta label="IP address" value={row.ip_address} />
          <Meta label="User-Agent" value={row.user_agent} />
          <div>
            <div
              className="text-[10px] uppercase tracking-[0.12em] font-semibold mb-1"
              style={{ color: "var(--ds-color-ink-subtle)" }}
            >
              Changes
            </div>
            <pre
              className="rounded-[var(--ds-radius-input)] p-3 text-xs overflow-x-auto leading-relaxed font-mono"
              style={{
                background: "var(--ds-color-admin-surface-muted)",
                border: "1px solid var(--ds-color-admin-border)",
                color: "var(--ds-color-ink)",
              }}
            >
              {JSON.stringify(row.changes || {}, null, 2)}
            </pre>
          </div>
        </div>
      </div>
    </div>
  );
}

function Meta({ label, value }) {
  return (
    <div>
      <div
        className="text-[10px] uppercase tracking-[0.12em] font-semibold"
        style={{ color: "var(--ds-color-ink-subtle)" }}
      >
        {label}
      </div>
      <div className="text-sm break-all" style={{ color: "var(--ds-color-ink)" }}>
        {value || "—"}
      </div>
    </div>
  );
}
